import React, { useState, useEffect } from 'react';
import axios from '../../lib/axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, Calendar, Activity, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';

const AiUsageChart = () => {
  const [data, setData] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());

  useEffect(() => {
    fetchUsage();
  }, [month, year]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/leads/settings/usage', {
        params: { month, year }
      });
      
      // Format data for chart
      const formattedData = response.data.daily_usage.map(item => ({
        date: dayjs(item.date).format('M-D'), // e.g., 5-1 for May 1st
        tokens: parseInt(item.total_tokens, 10),
        prompt: parseInt(item.prompt_tokens, 10),
        completion: parseInt(item.completion_tokens, 10),
      }));

      setData(formattedData);
      setTotalTokens(response.data.total_tokens);
    } catch (error) {
      console.error('Error fetching AI usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' }, { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  return (
    <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg text-slate-900 dark:text-white transition-all">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-xl font-black tracking-tight flex items-center gap-2">
             <Activity className="text-primary" size={20} />
             Consumo de Tokens
          </h2>
          <p className="text-slate-500 dark:text-gray-400 text-sm mt-1 font-medium">Historial mensual de uso de la IA</p>
        </div>
        
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2">
            <Calendar size={16} className="text-slate-400" />
            <select 
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="bg-transparent text-sm font-bold focus:outline-none cursor-pointer appearance-none text-slate-900 dark:text-white pr-2"
            >
                {months.map(m => (
                    <option key={m.value} value={m.value} className="text-slate-900 dark:text-slate-900">{m.label}</option>
                ))}
            </select>
            <span className="text-slate-300 dark:text-gray-600">|</span>
            <select 
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="bg-transparent text-sm font-bold focus:outline-none cursor-pointer appearance-none text-slate-900 dark:text-white"
            >
                {[year - 1, year, year + 1].map(y => (
                    <option key={y} value={y} className="text-slate-900 dark:text-slate-900">{y}</option>
                ))}
            </select>
            <ChevronDown size={14} className="text-slate-400" />
        </div>
      </div>

      <div className="mb-8 p-6 bg-primary/5 rounded-2xl border border-primary/10">
        <p className="text-xs text-slate-500 dark:text-gray-400 font-black uppercase tracking-widest mb-1">Total del Mes</p>
        <p className="text-4xl font-black text-primary">{totalTokens.toLocaleString()}</p>
      </div>

      <div className="h-[250px] w-full mt-4">
        {loading ? (
           <div className="h-full w-full flex items-center justify-center">
             <Loader2 className="animate-spin text-primary" size={32} />
           </div>
        ) : data.length === 0 ? (
            <div className="h-full w-full flex items-center justify-center border-2 border-dashed border-slate-100 dark:border-white/5 rounded-3xl">
                <p className="text-slate-400 dark:text-gray-500 font-bold uppercase tracking-widest text-xs">Sin registros para este periodo</p>
            </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                dy={10}
              />
              <YAxis hide />
              <Tooltip 
                cursor={{ fill: 'currentColor', opacity: 0.03 }}
                contentStyle={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '1.5rem', 
                    border: 'none',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                    padding: '1rem'
                }}
                itemStyle={{ color: '#000', fontWeight: '900', fontSize: '1.125rem' }}
                labelStyle={{ color: '#64748b', fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}
                formatter={(value) => [value.toLocaleString(), 'Tokens']}
              />
              <Bar dataKey="tokens" fill="#000" radius={[6, 6, 0, 0]} className="dark:fill-white" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default AiUsageChart;
